import { useState } from "react";
import { motion } from "framer-motion";
import { CARD, CARD3, BD, TX, TX2, GR, BL, YL, RD } from "../../utils/constants";
import { scoreColor, biasColor, fmt, getInvestmentAdvice, sanitizeDecimalInput } from "../../utils/formatUtils";

function QuickTrack({ symbol, onAdd }) {
  const [price, setPrice] = useState("");
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
      <input
        type="text"
        inputMode="decimal"
        value={price}
        onChange={(e) => setPrice(sanitizeDecimalInput(e.target.value))}
        placeholder="entry $"
        style={{ width: 78, background: CARD3, border: `1px solid ${BD}`, borderRadius: 5, padding: "5px 7px", color: TX, fontSize: 9 }}
      />
      <button
        className="btn"
        onClick={() => { if (price) { onAdd(symbol, price); setPrice(""); } }}
        style={{ background: BL + "18", border: `1px solid ${BL}40`, borderRadius: 5, padding: "5px 9px", color: BL, fontSize: 8, fontWeight: 700 }}
      >
        Track
      </button>
    </div>
  );
}

export default function WatchlistTab({ watchlist, investments, scannedStocks, onToggleWL, onStockClick, onAddInvestment, onRemoveInvestment }) {
  const symbols = Array.from(new Set([...(watchlist || []), ...Object.keys(investments || {})]));

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
        style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 900, fontSize: 22, letterSpacing: 1, background: `linear-gradient(90deg, ${YL}, ${BL})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          WATCHLIST
        </div>
        <div style={{ fontSize: 8, color: TX2, letterSpacing: 3, marginTop: 4 }}>{symbols.length} STOCK{symbols.length === 1 ? "" : "S"} TRACKED</div>
        <div style={{ height: 1, background: `linear-gradient(90deg, ${YL}40, transparent)`, marginTop: 8 }} />
      </motion.div>

      {symbols.length === 0 ? (
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
          className="ts-panel"
          style={{ background: CARD, border: `1px solid ${BD}`, borderRadius: 10, padding: 44, textAlign: "center", color: TX2 }}>
          <div style={{ fontSize: 12, color: TX, marginBottom: 5 }}>Watchlist empty</div>
          <div style={{ fontSize: 9 }}>Hit ☆ on any stock, or add an entry price here once you've scanned, to start tracking it.</div>
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}
          className="ts-panel"
          style={{ background: CARD, border: `1px solid ${BD}`, borderRadius: 10, overflow: "hidden" }}>
          {symbols.map((sym, i) => {
            const s = scannedStocks?.find((st) => st.symbol === sym);
            const inv = investments?.[sym];
            const advice = inv && s ? getInvestmentAdvice(inv.entryPrice, s.price, s) : null;
            const verdictColor = advice ? (advice.verdict === "SELL" ? RD : GR) : TX2;
            const isWatchlisted = (watchlist || []).includes(sym);

            return (
              <motion.div
                key={sym}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.22, delay: Math.min(i * 0.045, 0.4), ease: "easeOut" }}
                className={s ? "hov-row" : ""}
                onClick={() => s && onStockClick(s)}
                style={{ display: "flex", alignItems: "center", gap: 11, padding: "12px 14px", borderBottom: `1px solid ${BD}`, borderLeft: s ? `3px solid ${scoreColor(s.score)}` : `3px solid ${BD}`, flexWrap: "wrap" }}
              >
                <div style={{ fontWeight: 700, fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, color: TX, minWidth: 58 }}>{sym}</div>

                {s ? (
                  <>
                    <div style={{ minWidth: 90, fontSize: 8, color: TX2 }}>{s.name}</div>
                    <div style={{ fontSize: 11, color: TX, minWidth: 64 }}>${s.price?.toFixed(2)}</div>
                    <div style={{ fontSize: 10, color: s.change >= 0 ? GR : RD, fontWeight: 600, minWidth: 58 }}>{fmt(s.change || 0)}</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: scoreColor(s.score), fontFamily: "'Space Grotesk',sans-serif", minWidth: 28 }}>{s.score}</div>
                    <div style={{ fontSize: 8, color: biasColor(s.tomorrowBias), minWidth: 56 }}>{s.tomorrowBias}</div>
                  </>
                ) : <div style={{ flex: 1, fontSize: 8, color: TX2 }}>Run a scan to see live data</div>}

                <div style={{ flex: 1, minWidth: 0 }} />

                {inv ? (
                  <>
                    <div style={{ fontSize: 8, color: TX2, minWidth: 60 }}>entry ${inv.entryPrice.toFixed(2)}</div>
                    {advice ? (
                      <>
                        <div style={{ fontSize: 10, fontWeight: 700, color: advice.pnlPct >= 0 ? GR : RD, minWidth: 48, whiteSpace: "nowrap" }}>
                          {advice.pnlPct >= 0 ? "+" : ""}{advice.pnlPct.toFixed(1)}%
                        </div>
                        <div style={{ fontSize: 8, fontWeight: 800, color: verdictColor, background: verdictColor + "15", border: `1px solid ${verdictColor}40`, borderRadius: 5, padding: "3px 7px", whiteSpace: "nowrap" }}>
                          {advice.verdict}
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: 8, color: TX2, whiteSpace: "nowrap" }}>scan for verdict</div>
                    )}
                    <span onClick={(e) => { e.stopPropagation(); onRemoveInvestment(sym); }} style={{ fontSize: 10, color: TX2, cursor: "pointer", padding: "0 2px" }} title="Stop tracking">✕</span>
                  </>
                ) : s ? (
                  <QuickTrack symbol={sym} onAdd={onAddInvestment} />
                ) : (
                  <div style={{ fontSize: 8, color: TX2 }}>scan to add entry price</div>
                )}

                <span className="star" onClick={(e) => { e.stopPropagation(); onToggleWL(sym); }} style={{ fontSize: 12, color: isWatchlisted ? YL : TX2 }}>{isWatchlisted ? "★" : "☆"}</span>
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}
